<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { endOfDay, format, startOfDay, subDays } from 'date-fns';
import {
  pauseSessionsAPI,
  pauseRulesAPI,
} from 'dashboard/api/talkviaAgentPauses';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import WootDatePicker from 'dashboard/components/ui/DatePicker/DatePicker.vue';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';

const store = useStore();
const { t } = useI18n();

const currentUser = computed(() => store.getters.getCurrentUser);
const isAdmin = computed(() =>
  ['administrator', 'super_admin'].includes(currentUser.value?.role)
);
const agentsList = computed(() => store.getters['agents/getAgents'] || []);

// Date range
const dateRange = ref([
  startOfDay(subDays(new Date(), 29)),
  endOfDay(new Date()),
]);
const rangeType = ref(DATE_RANGE_TYPES.LAST_30_DAYS);

// Other filters
const selectedAgentId = ref('');
const selectedRuleId = ref('');
const page = ref(1);
const perPage = 25;

// Data
const report = ref(null);
const sessions = ref([]);
const totalSessions = ref(0);
const totalPages = ref(0);
const rules = ref([]);
const isLoading = ref(false);
const reportErrorDialogRef = ref(null);
const expandedGroups = ref([]);

const totalSeconds = computed(
  () =>
    report.value?.total_seconds ??
    Math.round((report.value?.total_minutes ?? 0) * 60)
);
const avgSeconds = computed(
  () =>
    report.value?.avg_seconds ??
    Math.round((report.value?.avg_minutes ?? 0) * 60)
);
const sessionCount = computed(() => report.value?.session_count ?? 0);
const byRule = computed(() => report.value?.by_rule ?? []);

const agentOptions = computed(() => [
  { value: '', label: t('AGENT_PAUSE_REPORT.ALL') },
  ...agentsList.value.map(agent => ({
    value: agent.id,
    label: agent.name,
  })),
]);

const ruleOptions = computed(() => [
  { value: '', label: t('AGENT_PAUSE_REPORT.ALL') },
  ...rules.value.map(rule => ({
    value: rule.id,
    label: rule.name,
  })),
]);

const durationInSeconds = session =>
  session.duration_seconds ?? Math.round((session.duration_minutes ?? 0) * 60);

const fmtDuration = seconds => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const remainingSeconds = total % 60;

  return `${hours}:${String(minutes).padStart(2, '0')}:${String(
    remainingSeconds
  ).padStart(2, '0')}`;
};

const fmtDate = str => {
  if (!str) return '-';
  return new Date(str).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fmtGroupDate = str => str.split('-').reverse().join('/');

const sessionGroups = computed(() => {
  const groupedSessions = new Map();

  sessions.value.forEach(session => {
    const dateKey = format(new Date(session.started_at), 'yyyy-MM-dd');
    const ruleKey = session.rule?.id ?? session.rule_name ?? 'ad-hoc';
    const key = `${dateKey}:${ruleKey}`;

    if (!groupedSessions.has(key)) {
      groupedSessions.set(key, {
        key,
        date: dateKey,
        ruleName: session.rule_name || t('AGENT_PAUSE_REPORT.AD_HOC'),
        sessions: [],
        totalSeconds: 0,
      });
    }

    const group = groupedSessions.get(key);
    group.sessions.push(session);
    group.totalSeconds += durationInSeconds(session);
  });

  return [...groupedSessions.values()];
});

const isGroupExpanded = key => expandedGroups.value.includes(key);

const toggleGroup = key => {
  expandedGroups.value = isGroupExpanded(key)
    ? expandedGroups.value.filter(groupKey => groupKey !== key)
    : [...expandedGroups.value, key];
};

const toIso = date => (date ? new Date(date).toISOString() : undefined);

const load = async () => {
  isLoading.value = true;
  try {
    const [start, end] = dateRange.value;
    const { data } = await pauseSessionsAPI.report({
      from: toIso(start),
      to: toIso(end),
      user_id:
        isAdmin.value && selectedAgentId.value
          ? selectedAgentId.value
          : undefined,
      rule_id: selectedRuleId.value || undefined,
      page: page.value,
    });
    report.value = data;
    sessions.value = data.sessions || [];
    expandedGroups.value = [];
    totalSessions.value = data.total_sessions || 0;
    totalPages.value = Math.ceil(totalSessions.value / perPage);
  } catch {
    reportErrorDialogRef.value?.open();
  } finally {
    isLoading.value = false;
  }
};

const loadRules = async () => {
  try {
    const { data } = await pauseRulesAPI.get();
    rules.value = data || [];
  } catch {
    /* ignore */
  }
};

const closeReportError = () => reportErrorDialogRef.value?.close();

const onDateRangeChanged = ([start, end, type]) => {
  dateRange.value = [start, end];
  rangeType.value = type;
  page.value = 1;
  load();
};

const applyFilters = () => {
  page.value = 1;
  load();
};

const clearFilters = () => {
  selectedAgentId.value = '';
  selectedRuleId.value = '';
  dateRange.value = [startOfDay(subDays(new Date(), 29)), endOfDay(new Date())];
  rangeType.value = DATE_RANGE_TYPES.LAST_30_DAYS;
  page.value = 1;
  load();
};

watch(page, load);

onMounted(() => {
  if (isAdmin.value) store.dispatch('agents/get');
  loadRules();
  load();
});
</script>

<template>
  <div class="flex min-h-0 w-full flex-col overflow-y-auto">
    <div class="mx-auto w-full max-w-5xl px-3 py-4 sm:px-6 sm:py-6">
      <!-- Header -->
      <div class="mb-6">
        <h2 class="text-xl font-semibold text-n-slate-12">
          {{ t('AGENT_PAUSE_REPORT.TITLE') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-10">
          {{
            t(
              isAdmin
                ? 'AGENT_PAUSE_REPORT.DESCRIPTION_ADMIN'
                : 'AGENT_PAUSE_REPORT.DESCRIPTION_AGENT'
            )
          }}
        </p>
      </div>

      <!-- Filters -->
      <div class="mb-5 rounded-xl border border-n-weak bg-n-solid-2 p-4">
        <div
          class="grid grid-cols-1 items-end gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(22rem,1.5fr)_minmax(9rem,0.7fr)_minmax(10rem,0.8fr)_auto]"
        >
          <div
            class="flex min-w-0 flex-col gap-1.5 sm:col-span-2 xl:col-span-1"
          >
            <label class="text-heading-3 text-n-slate-12">
              {{ t('AGENT_PAUSE_REPORT.PERIOD') }}
            </label>
            <WootDatePicker
              v-model:date-range="dateRange"
              v-model:range-type="rangeType"
              class="pause-report-date-picker"
              teleport-to-body
              @date-range-changed="onDateRangeChanged"
            />
          </div>

          <!-- Agent filter (admin only) -->
          <div v-if="isAdmin" class="flex min-w-0 flex-col gap-1.5">
            <label class="text-heading-3 text-n-slate-12">
              {{ t('AGENT_PAUSE_REPORT.AGENT') }}
            </label>
            <Select
              v-model="selectedAgentId"
              :options="agentOptions"
              class="w-full [&>select]:w-full"
            />
          </div>

          <!-- Rule filter -->
          <div class="flex min-w-0 flex-col gap-1.5">
            <label class="text-heading-3 text-n-slate-12">
              {{ t('AGENT_PAUSE_REPORT.PAUSE_TYPE') }}
            </label>
            <Select
              v-model="selectedRuleId"
              :options="ruleOptions"
              class="w-full [&>select]:w-full"
            />
          </div>

          <!-- Actions -->
          <div
            class="flex flex-wrap gap-2 self-end sm:col-span-2 xl:col-span-1"
          >
            <Button
              type="button"
              :label="t('AGENT_PAUSE_REPORT.FILTER')"
              class="flex-1 sm:flex-none"
              @click="applyFilters"
            />
            <Button
              type="button"
              :label="t('AGENT_PAUSE_REPORT.CLEAR')"
              variant="ghost"
              color="slate"
              class="flex-1 sm:flex-none"
              @click="clearFilters"
            />
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="isLoading" class="flex items-center justify-center py-14">
        <span class="i-lucide-loader-2 size-6 animate-spin text-n-slate-10" />
      </div>

      <template v-else>
        <!-- Summary cards -->
        <div class="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
          <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
            <p class="text-xs font-medium text-n-slate-10 mb-1">
              {{ t('AGENT_PAUSE_REPORT.TOTAL_PAUSES') }}
            </p>
            <p class="text-2xl font-bold text-n-slate-12">{{ sessionCount }}</p>
          </div>
          <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
            <p class="text-xs font-medium text-n-slate-10 mb-1">
              {{ t('AGENT_PAUSE_REPORT.TOTAL_TIME') }}
            </p>
            <p class="text-2xl font-bold text-n-slate-12">
              {{ fmtDuration(totalSeconds) }}
            </p>
          </div>
          <div class="rounded-xl border border-n-weak bg-n-solid-2 p-4">
            <p class="text-xs font-medium text-n-slate-10 mb-1">
              {{ t('AGENT_PAUSE_REPORT.AVERAGE_DURATION') }}
            </p>
            <p class="text-2xl font-bold text-n-slate-12">
              {{ fmtDuration(avgSeconds) }}
            </p>
          </div>
        </div>

        <!-- By rule breakdown -->
        <div
          v-if="byRule.length"
          class="mb-5 rounded-xl border border-n-weak bg-n-solid-2 overflow-hidden"
        >
          <div class="px-5 py-3 border-b border-n-weak">
            <p class="text-sm font-semibold text-n-slate-12">
              {{ t('AGENT_PAUSE_REPORT.BY_TYPE') }}
            </p>
          </div>
          <div class="divide-y divide-n-weak">
            <div
              v-for="item in byRule"
              :key="item.rule_id ?? 'no-rule'"
              class="flex flex-wrap items-center justify-between gap-2 px-4 py-3 sm:px-5"
            >
              <span class="text-sm text-n-slate-12">{{
                item.rule_name || t('AGENT_PAUSE_REPORT.NO_RULE')
              }}</span>
              <div class="flex items-center gap-4">
                <span class="text-xs text-n-slate-10">
                  {{
                    t('AGENT_PAUSE_REPORT.PAUSE_COUNT', {
                      count: item.count,
                    })
                  }}
                </span>
                <span class="text-sm font-medium text-n-slate-12">{{
                  fmtDuration(item.total_seconds ?? item.total_minutes * 60)
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Sessions list -->
        <div
          class="rounded-xl border border-n-weak bg-n-solid-2 overflow-hidden"
        >
          <div
            class="px-5 py-3 border-b border-n-weak flex items-center justify-between"
          >
            <p class="text-sm font-semibold text-n-slate-12">
              {{ t('AGENT_PAUSE_REPORT.SESSIONS') }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{
                t('AGENT_PAUSE_REPORT.RECORD_COUNT', {
                  count: totalSessions,
                })
              }}
            </p>
          </div>

          <div
            v-if="!sessions.length"
            class="flex items-center justify-center py-10"
          >
            <p class="text-sm text-n-slate-10">
              {{ t('AGENT_PAUSE_REPORT.EMPTY') }}
            </p>
          </div>

          <div v-else class="divide-y divide-n-weak">
            <div v-for="group in sessionGroups" :key="group.key">
              <div
                class="flex flex-col gap-3 px-4 py-3 transition-colors hover:bg-n-alpha-1 sm:flex-row sm:items-center sm:px-5"
              >
                <div class="flex min-w-0 flex-1 items-center gap-3">
                  <div
                    class="flex size-9 shrink-0 items-center justify-center rounded-lg bg-n-blue-3 text-n-blue-11"
                  >
                    <span class="i-lucide-calendar-days size-4" />
                  </div>
                  <div class="min-w-0">
                    <p class="text-sm font-medium text-n-slate-12">
                      {{ fmtGroupDate(group.date) }}
                    </p>
                    <p class="truncate text-xs text-n-slate-10">
                      {{
                        t('AGENT_PAUSE_REPORT.GROUP_SUMMARY', {
                          name: group.ruleName,
                          count: group.sessions.length,
                        })
                      }}
                    </p>
                  </div>
                </div>
                <div
                  class="flex items-center justify-between gap-3 sm:justify-end"
                >
                  <div class="text-left sm:text-right">
                    <p class="text-[11px] text-n-slate-10">
                      {{ t('AGENT_PAUSE_REPORT.TOTAL_DURATION') }}
                    </p>
                    <p
                      class="font-mono text-sm font-semibold tabular-nums text-n-slate-12"
                    >
                      {{ fmtDuration(group.totalSeconds) }}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    color="slate"
                    :icon="
                      isGroupExpanded(group.key)
                        ? 'i-lucide-chevron-up'
                        : 'i-lucide-chevron-down'
                    "
                    :aria-label="
                      isGroupExpanded(group.key)
                        ? t('AGENT_PAUSE_REPORT.COLLAPSE')
                        : t('AGENT_PAUSE_REPORT.EXPAND')
                    "
                    @click="toggleGroup(group.key)"
                  />
                </div>
              </div>

              <div
                v-if="isGroupExpanded(group.key)"
                class="overflow-x-auto overscroll-x-contain border-t border-n-weak bg-n-alpha-1"
              >
                <div class="min-w-[680px]">
                  <div
                    class="grid grid-cols-[1.2fr_1fr_1fr_auto] gap-3 px-5 py-2"
                  >
                    <span class="text-label-3 text-n-slate-10">
                      {{ t('AGENT_PAUSE_REPORT.AGENT') }}
                    </span>
                    <span class="text-label-3 text-n-slate-10">
                      {{ t('AGENT_PAUSE_REPORT.START') }}
                    </span>
                    <span class="text-label-3 text-n-slate-10">
                      {{ t('AGENT_PAUSE_REPORT.END') }}
                    </span>
                    <span class="text-label-3 text-right text-n-slate-10">
                      {{ t('AGENT_PAUSE_REPORT.DURATION') }}
                    </span>
                  </div>
                  <div
                    v-for="session in group.sessions"
                    :key="session.id"
                    class="grid grid-cols-[1.2fr_1fr_1fr_auto] gap-3 border-t border-n-weak px-5 py-2.5"
                  >
                    <span class="truncate text-sm text-n-slate-12">
                      {{ session.agent_name || '-' }}
                    </span>
                    <span class="text-sm text-n-slate-10">
                      {{ fmtDate(session.started_at) }}
                    </span>
                    <span class="text-sm text-n-slate-10">
                      {{ fmtDate(session.ended_at) }}
                    </span>
                    <span
                      class="text-right font-mono text-sm font-medium tabular-nums text-n-slate-12"
                    >
                      {{ fmtDuration(durationInSeconds(session)) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div
          v-if="totalPages > 1"
          class="mt-4 flex items-center justify-center gap-2"
        >
          <button
            type="button"
            class="h-8 px-3 rounded-lg border border-n-weak text-sm text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40 transition-colors"
            :disabled="page === 1"
            @click="page--"
          >
            {{ t('AGENT_PAUSE_REPORT.PREVIOUS') }}
          </button>
          <span class="text-sm text-n-slate-10">
            {{ `${page} / ${totalPages}` }}
          </span>
          <button
            type="button"
            class="h-8 px-3 rounded-lg border border-n-weak text-sm text-n-slate-11 hover:bg-n-alpha-2 disabled:opacity-40 transition-colors"
            :disabled="page >= totalPages"
            @click="page++"
          >
            {{ t('AGENT_PAUSE_REPORT.NEXT') }}
          </button>
        </div>
      </template>
    </div>
    <Dialog
      ref="reportErrorDialogRef"
      type="alert"
      width="sm"
      :title="t('AGENT_PAUSE_REPORT.ERROR_TITLE')"
      :description="t('AGENT_PAUSE_REPORT.ERROR_DESCRIPTION')"
      :confirm-button-label="t('AGENT_PAUSE_REPORT.UNDERSTOOD')"
      :show-cancel-button="false"
      :close-on-click-outside="false"
      @confirm="closeReportError"
    />
  </div>
</template>
